import { type ComponentType, StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { type EnvSource, MissingEnvError, parseEnv } from '../config/parseEnv';
import ConfigError from './ConfigError';
import LoadError from './LoadError';

export type AppLoader = () => Promise<ComponentType>;

/**
 * Validate the configuration before loading the app. The app's module graph
 * reads `config/env.ts`, which throws on import when a variable is missing;
 * checking first turns that into a readable screen instead of a blank page.
 * A failed download of the app itself gets a screen too, for the same reason.
 */
export async function bootstrap(
  container: HTMLElement,
  source: EnvSource,
  loadApp: AppLoader
): Promise<Root> {
  const root = createRoot(container);

  try {
    parseEnv(source);
  } catch (error) {
    if (!(error instanceof MissingEnvError)) {
      throw error;
    }
    console.error(error.message);
    root.render(<ConfigError missing={error.missing} />);
    return root;
  }

  let App: ComponentType;
  try {
    App = await loadApp();
  } catch (error) {
    console.error(error);
    root.render(<LoadError />);
    return root;
  }

  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
  return root;
}
